import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import useQuery from 'hooks/useQuery';

import { Button, Typography, Link } from 'ui-kit';
import './SignUpPage.scss';
import { requestMagicLink, requestSignup } from 'api/auth';
import useSocialAuth from 'hooks/useSocialAuth';
import SignUpLayout from 'components/SignUpLayout';
import UnauthLayout from 'components/UnauthLayout';
import SignUpForm from './SignUpForm';
import { FormProvider, useForm } from 'react-hook-form';
import { signupResolver } from './utils';

import { useAnalytics } from 'analytics';
import CheckInbox from 'pages/magic-link/CheckInbox';
import { getLinkRequestError } from 'pages/magic-link/utils';
import { markSignupPending } from 'utils/signupPending';
import { getToltReferral } from 'utils/integrations';

const SignUpPage = () => {
  const capture = useAnalytics();
  const [sentEmail, setSentEmail] = useState('');
  // Not dead: on mount it shows the error that a social sign-in sends back in the query.
  useSocialAuth();

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      email: '',
      phone: '',
      marketingCheckbox: true,
      captchaToken: '',
    },
    resolver: signupResolver,
  });

  const queryParams = useQuery();
  const { search } = useLocation();

  // The API gives the same answer for a new email and a known one, so nothing here may tell
  // them apart.
  const create = (payload) =>
    requestSignup({ ...payload, ...getToltReferral() })
      .then(() => {
        capture('signup_link_requested', {
          has_next_param: Boolean(queryParams.next),
          marketing_consent: payload.marketingCheckbox,
        });
        setSentEmail(payload.email);
        markSignupPending();
      })
      .catch((error) => {
        const { reason, message: text } = getLinkRequestError(error);

        capture('signup_request_failed', { reason });
        message.error(text);
      });

  const resend = () =>
    requestMagicLink({ email: sentEmail })
      .then(() => {
        capture('magic_link_requested', { source: 'signup', is_resend: true });
        return true;
      })
      .catch((error) => {
        message.error(getLinkRequestError(error).message);
        return false;
      });

  if (sentEmail) {
    return (
      <UnauthLayout compact>
        <CheckInbox email={sentEmail} onResend={resend} onChangeEmail={() => setSentEmail('')} />
      </UnauthLayout>
    );
  }

  return (
    <SignUpLayout title="Get Started Now">
      <Typography variant="body1" className="SignupPage-signin">
        Already have an account?{' '}
        <Button to={`/login/${search}`} className="SignupPage-singin-button">
          Log in
        </Button>
      </Typography>
      <FormProvider {...methods}>
        <SignUpForm onSubmit={create} />
      </FormProvider>
      <Typography color="textGray" variant="caption">
        By continuing, you agree to The Host Co{' '}
        <Link className="Link-sign-up" href="https://www.thehost.co/terms-conditions">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link className="Link-sign-up" href="https://www.thehost.co/privacy-policy">
          Privacy Policy
        </Link>
        .
      </Typography>
    </SignUpLayout>
  );
};

export default SignUpPage;
